import {ResourceTypeEnum} from "../base/training-literals";
import {text_pickSurfaceNormal} from "../../../../../js/text_message";
import {CirclePlane} from "./animation/CirclePlane";
import {PivotPoint} from "./animation/PivotPoint";
import _ from "lodash";

export class AnimationResource {
    constructor() {
        this.type = ResourceTypeEnum.ANIMATION;
        this.normalPlane = null;
        this.pivotPoint = null;
        this.modelId = null;
        this.positionId = null;
        this.strategy = '';
        this.circlePointEvents = null;
        this.circlePointObjects = [];
        this.registerNormalCallback = this.registerNormalHandler.bind(this);
        this.registerPivotCallback = this.registerPivotHandler.bind(this);
        this.registerReverseCallback = this.registerReverseHandler.bind(this);
        this.cameraEventCallback = (configuration) => this.updateWithCamera(configuration);
        iq3Camera.addEventListener('change', this.cameraEventCallback);

        this.normalInstructionMessage = _.throttle(this.normalInstructionMessage.bind(this), 5000);
        this.pivotInstructionMessage = _.throttle(this.pivotInstructionMessage.bind(this), 5000);

        wv.communicator.addEventListener(text_pickSurfaceNormal, this.registerNormalCallback);
        wv.communicator.addEventListener(text_pickPivot, this.registerPivotCallback);
        wv.communicator.addEventListener(text_reverseSurfaceNormal, this.registerReverseCallback);
    }

    registerNormalHandler(itemArr) {
        this.resetData();
        const state = itemArr[1];
        if (state !== 'ready') {
            return;
        }

        this.positionId = itemArr[0];
        this.strategy = itemArr[2];

        if (this.strategy === 'normal') {
            VRMessageController.addMessage({text: "Click on a surface to select the normal"});
        } else if (this.strategy === 'circle') {
            VRMessageController.addMessage({text: "Select 3 points on the circle perimeter"});
        }

        manageFeatureAndCallbackHandler(
            wv.mouseClickCallbackHandler,
            {
                callback: this.clickedOnSurface,
                context: this,
            },
            true,
            "",
            () => {
            },
            true,
            true
        );
        manageFeatureAndCallbackHandler(
            wv.controllerTriggerDownCallbackhandler,
            {
                callback: this.clickedOnSurface,
                context: this,
            },
            true,
            "",
            () => {
            },
            true,
            true
        );
    }

    removeNormalHandler() {
        manageFeatureAndCallbackHandler(
            wv.mouseClickCallbackHandler,
            {
                callback: this.clickedOnSurface,
                context: this,
            },
            false,
            "",
            () => {
            },
            false,
            true
        );
        manageFeatureAndCallbackHandler(
            wv.controllerTriggerDownCallbackhandler,
            {
                callback: this.clickedOnSurface,
                context: this,
            },
            false,
            "",
            () => {
            },
            false,
            true
        );
    }

    normalInstructionMessage() {
        if (this.strategy === 'normal') {
            VRMessageController.addMessage({text: "Confirm selection or click on a surface again to change the selection"});
        } else if (this.strategy === 'circle') {
            VRMessageController.addMessage({ text: "Confirm selection or click on a surface to restart the circle selection" });
        }
    }

    clickedOnSurface(event) {
        if (!(event?.pickingData?.volume)) {
            return;
        }

        let normal, sendData = false;
        this.normalPlane?.destroy();
        if (this.strategy === 'normal') {
            if (event.pickingData.object.isPointCloudModel) {
                VRMessageController.addMessage({text: "Can't pick axis of rotation from point cloud surface 'normal'. Use circle method to pick axis of rotation."});
                return;
            }
            this.normalPlane = new AlignmentPlane({...event.pickingData});
            VR.scene.add(this.normalPlane);

            const data = this.normalPlane.getNormal();
            normal = data.normal;
            sendData = true;
            this.normalInstructionMessage();
        } else if (this.strategy === 'circle') {
            if (this.circlePointEvents !== null && this.circlePointEvents[0].modelId !== event.pickingData.modelId) {
                VRMessageController.addMessage({text: "Can't pick circle points from multiple models. Selection Restarted. Select 3 points on the circle perimeter"});
                this.circlePointEvents = null;
                this.clearPoints();
                return;
            }

            if (this.circlePointEvents === null) {
                this.circlePointEvents = [{...event.pickingData}];
                this.showPoint(event);
            } else if (this.circlePointEvents.length < 3) {
                this.circlePointEvents.push({...event.pickingData});
                this.showPoint(event);
            }

            if (this.circlePointEvents?.length === 3) {
                try {
                    this.normalPlane = new CirclePlane(this.circlePointEvents, event.pickingData.modelId);
                    VR.scene.add(this.normalPlane);

                    const data = this.normalPlane.getNormal();
                    normal = data.normal;
                    sendData = true;

                    this.normalInstructionMessage();
                } catch (e) {
                    VRMessageController.addMessage({text: `${e.message} Try again by selecting 3 points`});
                }
                this.circlePointEvents = null;
                this.clearPoints();
            }
        }

        if (sendData) {
            const payload = {
                message: text_pickSurfaceNormal.command,
                positionId: this.positionId,
                strategy: this.strategy,
                normal,
            };
            wv.communicator.socket.communicateWithContainer(payload);
        }
    }

    showPoint(event) {
        const point = BasicShapes.sphere;
        point.position.copy(event.pickingData.point);
        this.circlePointObjects.push(point);
        VR.scene.add(point);
        this.updateWithCamera(iq3Camera.getConfigurationWorld());
    }

    registerPivotHandler(itemArr) {
        this.resetData();
        const state = itemArr[0];
        if (state !== 'ready') {
            return;
        }

        VRMessageController.addMessage({text: "Click on a surface to select the pivot point"});
        manageFeatureAndCallbackHandler(
            wv.mouseClickCallbackHandler,
            {
                callback: this.pivotPicked,
                context: this,
            },
            true,
            "",
            () => {
            },
            true,
            true
        );
        manageFeatureAndCallbackHandler(
            wv.controllerTriggerDownCallbackhandler,
            {
                callback: this.pivotPicked,
                context: this,
            },
            true,
            "",
            () => {
            },
            true,
            true
        );
    }

    removePivotHandler() {
        manageFeatureAndCallbackHandler(
            wv.mouseClickCallbackHandler,
            {
                callback: this.pivotPicked,
                context: this,
            },
            false,
            "",
            () => {
            },
            false,
            true
        );
        manageFeatureAndCallbackHandler(
            wv.controllerTriggerDownCallbackhandler,
            {
                callback: this.pivotPicked,
                context: this,
            },
            false,
            "",
            () => {
            },
            false,
            true
        );
    }

    pivotInstructionMessage() {
        VRMessageController.addMessage({text: "Confirm or Click on a surface again to select the pivot point"});
    }

    pivotPicked(event) {
        if (!event?.pickingData?.volume) {
            return;
        }

        this.pivotPoint?.destroy();
        this.pivotPoint = new PivotPoint(event.pickingData, event.pickingData.modelId);
        VR.scene.add(this.pivotPoint);

        const {pivot} = this.pivotPoint.getPivot();
        const payload = {
            message: text_pickPivot.command,
            pivot,
        };
        wv.communicator.socket.communicateWithContainer(payload);
        this.pivotInstructionMessage();
    }

    registerReverseHandler(itemArr) {
        let positionId = itemArr[0];
        if (positionId !== this.positionId) {
            VRMessageController.addMessage({text: "Can't reverse normal for another position."});
            return;
        }

        this.normalPlane.reverseNormal();
        const {normal} = this.normalPlane.getNormal();

        const payload = {
            message: text_pickSurfaceNormal.command,
            positionId: this.positionId,
            strategy: this.strategy,
            normal,
        };
        wv.communicator.socket.communicateWithContainer(payload);
    }

    resetData() {
        this.removeNormalHandler();
        this.removePivotHandler();
        this.normalPlane?.destroy();
        this.normalPlane = null;
        this.pivotPoint?.destroy();
        this.pivotPoint = null;
        this.positionId = null;
        this.strategy = '';
        this.circlePointEvents = null;
        this.clearPoints();
    }

    updateWithCamera(configuration) {
        this.circlePointObjects.forEach(point => {
            const pointPosition = point.position.clone();
            const scale = 1.75 * Math.min(wv.maxDistanceForTooltipScalling, pointPosition.distanceTo(configuration.position)) * CurveWidthToObserverDistanceRatio;
            point.scale.set(scale, scale, scale);
        });
    }

    clearPoints() {
        this.circlePointObjects.forEach(point => {
            VR.scene.remove(point);
            disposeObject(point);
        });
        this.circlePointObjects = [];
    }

    async cancel() {
        this.resetData();
        wv.communicator.removeEventListener(text_pickSurfaceNormal, this.registerNormalCallback);
        wv.communicator.removeEventListener(text_pickPivot, this.registerPivotCallback);
        wv.communicator.removeEventListener(text_reverseSurfaceNormal, this.registerReverseCallback);
        iq3Camera.removeEventListener('change', this.cameraEventCallback);
    }
}
